import type { FC } from 'react';
import { PrivacyStyles as styles } from '../css/styles';
import { Helmet } from 'react-helmet-async';

const PrivacyUS: FC = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy — First Klass</title>
                <meta name="description" content="Privacy Policy for First Klass platform (www.first-klass.ru) — United States" />
            </Helmet>
            <div className={styles.privacyContainer}>
                <h1>Privacy Policy</h1>
                <p>
                    This Privacy Policy describes how <strong>First Klass</strong> (
                    <strong>www.first-klass.ru</strong>, hereinafter «we», «us», or «our»)
                    collects, uses, discloses, and safeguards personal information about users
                    in the United States. Where applicable, this Policy addresses requirements
                    under the California Consumer Privacy Act (CCPA/CPRA) and other applicable
                    US state privacy laws.
                </p>
                <p>
                    By accessing or using the Platform, you agree to the terms of this Privacy
                    Policy. If you disagree with any part of this Policy, please do not use
                    the Platform.
                </p>

                <h2>1. Information We Collect</h2>
                <p>We collect the following categories of personal information:</p>
                <ul>
                    <li>
                        <strong>Identifiers</strong> — name, email address, phone number,
                        account username, and IP address;
                    </li>
                    <li>
                        <strong>Account and profile information</strong> — profile details,
                        preferences, and settings you provide;
                    </li>
                    <li>
                        <strong>Internet or network activity</strong> — browsing history on the
                        Platform, pages viewed, features used, and session information;
                    </li>
                    <li>
                        <strong>Device and technical data</strong> — browser type, operating system,
                        device identifiers, and cookie data;
                    </li>
                    <li>
                        <strong>Communications</strong> — messages sent to us via support channels
                        or contact forms.
                    </li>
                </ul>
                <p>
                    We do not collect sensitive personal information such as Social Security numbers,
                    financial account details, precise geolocation, health data, or biometric data.
                </p>

                <h2>2. How We Collect Information</h2>
                <ul>
                    <li>
                        <strong>Directly from you</strong> — when you register, fill in forms,
                        or contact us;
                    </li>
                    <li>
                        <strong>Automatically</strong> — via cookies, web beacons, and similar
                        technologies when you use the Platform;
                    </li>
                    <li>
                        <strong>From third parties</strong> — such as authentication providers
                        or analytics services, subject to their own privacy policies.
                    </li>
                </ul>

                <h2>3. How We Use Your Information</h2>
                <ul>
                    <li>To create and manage your account and deliver Platform services;</li>
                    <li>To process payments and fulfil subscription orders;</li>
                    <li>To send service-related notifications and respond to your requests;</li>
                    <li>To send promotional communications where you have opted in;</li>
                    <li>To analyse usage patterns and improve Platform performance;</li>
                    <li>To detect, prevent, and address fraud, security, or technical issues;</li>
                    <li>To comply with applicable laws and legal obligations.</li>
                </ul>

                <h2>4. Disclosure of Your Information</h2>
                <p>We may share your personal information with:</p>
                <ul>
                    <li>
                        <strong>Service providers</strong> — third parties that perform services
                        on our behalf (hosting, analytics, payment processing, email delivery)
                        under confidentiality obligations;
                    </li>
                    <li>
                        <strong>Business transfers</strong> — in connection with a merger, acquisition,
                        or sale of assets, with notice to affected users;
                    </li>
                    <li>
                        <strong>Legal compliance</strong> — when required by law, court order,
                        or governmental authority, or to protect our legal rights.
                    </li>
                </ul>
                <p>We do not sell your personal information to third parties.</p>

                <h2>5. Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies and similar tracking technologies to operate the Platform and
                    improve your experience. You can manage cookie preferences through your browser
                    settings. Disabling certain cookies may affect Platform functionality.
                </p>
                <p>
                    We currently do not respond to «Do Not Track» (DNT) browser signals, as there
                    is no industry-wide standard for DNT compliance. We will update this Policy
                    if that changes.
                </p>

                <h2>6. Data Retention</h2>
                <p>
                    We retain your personal information for as long as your account is active or
                    as needed to provide services. Upon account deletion, we delete or de-identify
                    your personal data within a reasonable timeframe, except where retention is
                    required by law.
                </p>

                <h2>7. Your Privacy Rights</h2>
                <p>
                    Depending on your state of residence, you may have the following rights:
                </p>
                <ul>
                    <li>
                        <strong>Right to know</strong> — to request disclosure of the categories
                        and specific pieces of personal information we have collected about you;
                    </li>
                    <li>
                        <strong>Right to delete</strong> — to request deletion of personal information
                        we have collected, subject to certain exceptions;
                    </li>
                    <li>
                        <strong>Right to correct</strong> — to request correction of inaccurate
                        personal information;
                    </li>
                    <li>
                        <strong>Right to opt out</strong> — to opt out of the sale or sharing of
                        personal information (we do not sell personal data);
                    </li>
                    <li>
                        <strong>Right to non-discrimination</strong> — we will not discriminate
                        against you for exercising any of your privacy rights.
                    </li>
                </ul>
                <p>
                    To submit a privacy rights request, contact us through the Platform's contact
                    form. We will verify your identity and respond within 45 days, as required
                    by applicable law.
                </p>

                <h2>8. Children's Privacy</h2>
                <p>
                    The Platform is not directed at children under the age of 13. We do not
                    knowingly collect personal information from children under 13 without verifiable
                    parental consent. If you believe we have inadvertently collected such information,
                    please contact us and we will promptly delete it.
                </p>

                <h2>9. Security</h2>
                <p>
                    We implement reasonable administrative, technical, and physical safeguards
                    to protect your personal information. However, no method of transmission
                    over the internet is 100% secure. We encourage you to use a strong, unique
                    password and to contact us immediately if you suspect unauthorised access
                    to your account.
                </p>

                <h2>10. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy periodically. We will notify you of material
                    changes by posting the updated Policy on the Platform and, where required,
                    by email. The current version is always available at{' '}
                    <strong>www.first-klass.ru</strong>.
                </p>
                <p>
                    <strong>Version date: April 14, 2026</strong>
                </p>
            </div>
        </>
    );
};

export default PrivacyUS;
